import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { checkSecurityDomain } from '../utils/auth';
import { InsightsSummaryCard, SessionHistoryCard } from '../features/my-insights';
import { AnalyticsThemeProvider, categoryAccent } from '@repo/ui/theme';
import { DeviceConnections } from '../components/dashboard/devices/DeviceConnections';
import { useIsMobile } from '../hooks/useIsMobile';

export const Route = createFileRoute('/my-insights')({
  component: RouteComponent,
  beforeLoad: checkSecurityDomain({}),
});

const DEVICE_CONNECTION_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Device connection was cancelled.',
  invalid_request: 'Device connection request was invalid. Please try again.',
  unauthorized_client: 'Device connection is not authorized. Please try again.',
  server_error: 'The device provider ran into an error. Please try again.',
  oauth_failed: 'Device connection failed. Please try again.',
  oauth_error: 'Device connection failed. Please try again.',
};

function RouteComponent() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [deviceConnectionError, setDeviceConnectionError] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');

    if (error && DEVICE_CONNECTION_ERROR_MESSAGES[error]) {
      setDeviceConnectionError(DEVICE_CONNECTION_ERROR_MESSAGES[error]);

      const timer = setTimeout(() => {
        setDeviceConnectionError(null);
        navigate({ to: '/my-insights' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [navigate]);

  return (
    <AnalyticsThemeProvider accent={categoryAccent['session-performance']}>
      <div className="flex flex-col items-center py-10">
        {deviceConnectionError && (
          <div className="mb-4 w-full max-w-md rounded-md border border-red-200 bg-red-100 p-3 text-red-800 shadow-sm">
            <p className="text-center font-medium">{deviceConnectionError}</p>
          </div>
        )}
        <div className={`container mx-auto grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
          <div className={`flex flex-col gap-6 ${isMobile ? '' : 'col-span-2'}`}>
            <InsightsSummaryCard />
            <SessionHistoryCard />
          </div>
          <div className={isMobile ? '' : 'col-span-1'}>
            <DeviceConnections />
          </div>
        </div>
      </div>
    </AnalyticsThemeProvider>
  );
}
